
import React, { useState } from 'react';
import { Worry, Language } from '../types';
import { TRANSLATIONS } from '../constants';

interface Fragment {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  opacity: number;
  delay: number;
}

interface EnergyConversionBoxProps {
  onProceed?: () => void;
  language: Language;
}

const EnergyConversionBox: React.FC<EnergyConversionBoxProps> = ({ onProceed, language }) => {
  const [worryText, setWorryText] = useState('');
  const [worries, setWorries] = useState<Worry[]>([]);
  const [dust, setDust] = useState<Fragment[]>([]);
  const [hasReleased, setHasReleased] = useState(false);

  const t = TRANSLATIONS[language];

  const addWorry = () => {
    if (!worryText.trim()) return;
    const id = Math.random().toString(36).substr(2, 9);
    
    setHasReleased(true);
    setWorries(prev => [...prev, { id, text: worryText, x: 50, y: 50 }]);
    
    const particleCount = 220; 
    const newDust: Fragment[] = Array.from({ length: particleCount }).map(() => ({
      id: Math.random().toString(36).substr(2, 9),
      x: 50 + (Math.random() - 0.5) * 25, 
      y: 50 + (Math.random() - 0.5) * 8,
      vx: (Math.random() - 0.5) * 12, 
      vy: -(Math.random() * 6 + 3),  
      size: Math.random() * 2.5 + 0.5, 
      color: Math.random() > 0.4 ? '#93c5fd' : '#ffffff',
      opacity: 1,
      delay: Math.random() * 2.5 
    }));
    
    setTimeout(() => {
      setDust(prev => [...prev, ...newDust]);
    }, 1800);

    setWorryText('');

    setTimeout(() => {
      setWorries(p => p.filter(w => w.id !== id));
    }, 3800);
    
    setTimeout(() => {
      const dustIds = newDust.map(d => d.id);
      setDust(p => p.filter(d => !dustIds.includes(d.id)));
    }, 12000);
  };

  return (
    <div className="glass p-3 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] w-full relative border border-white/10 shadow-2xl overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
      
      <div className="mb-3 md:mb-6 text-center relative z-10">
        <div className="inline-block px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-2 md:mb-3">
          <span className="text-[7px] md:text-[8px] font-black text-blue-400 uppercase tracking-[0.3em] md:tracking-[0.4em]">{t.tools.box_node}</span>
        </div>
        <h3 className="font-mono-cia text-base md:text-xl mb-1 text-white uppercase tracking-tighter font-black">{t.tools.box_title}</h3>
        <p className="text-[9px] md:text-xs text-slate-400 font-medium leading-relaxed max-w-sm mx-auto italic opacity-70">
          {t.tools.box_subtitle}
        </p>
      </div>

      <div className="flex flex-col gap-2 md:gap-3 mb-4 md:mb-6 relative z-20 px-1 sm:px-0">
        <input 
          type="text" 
          value={worryText} 
          onChange={(e) => setWorryText(e.target.value)} 
          onKeyPress={(e) => e.key === 'Enter' && addWorry()} 
          placeholder={t.tools.box_placeholder} 
          className="bg-black/60 border border-white/10 rounded-xl md:rounded-2xl px-4 md:px-6 py-3 md:py-4 outline-none text-xs md:text-sm text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all font-mono-cia placeholder:text-slate-600 w-full" 
        />
        <button 
          onClick={addWorry} 
          className="bg-blue-600 hover:bg-blue-500 text-white w-full py-3 md:py-4 rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all active:scale-95 shadow-[0_10px_20px_rgba(37,99,235,0.2)]"
        >
          {t.tools.box_button}
        </button>
      </div>

      <div className="h-60 md:h-[450px] relative bg-black/50 rounded-2xl md:rounded-3xl border border-white/5 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
        
        <div className="absolute bottom-0 w-full h-24 bg-gradient-to-t from-blue-900/20 to-transparent border-t border-blue-500/10 z-10 flex flex-col items-center justify-end pb-4">
           <div className="w-24 h-0.5 bg-blue-500/20 rounded-full mb-1"></div>
           <span className="text-[7px] font-mono-cia text-blue-500/40 uppercase tracking-[0.5em] font-bold">{t.tools.box_containment}</span>
        </div>

        {worries.length === 0 && dust.length === 0 ? (
          <div className="flex flex-col items-center gap-4 text-center px-6">
             {hasReleased && onProceed ? (
               <button 
                 onClick={onProceed}
                 className="animate-fade-in group flex flex-col items-center gap-3 bg-blue-600/10 border border-blue-500/40 p-6 rounded-3xl hover:bg-blue-600 transition-all"
               >
                 <span className="text-4xl">🚀</span>
                 <div className="flex flex-col items-center">
                    <span className="text-xs font-black text-white uppercase tracking-widest">{t.tools.box_mind_cleared}</span>
                    <span className="text-[10px] text-blue-300 uppercase opacity-60">{t.tools.box_proceed}</span>
                 </div>
               </button>
             ) : (
               <div className="flex flex-col items-center gap-4 opacity-10">
                  <div className="w-12 h-12 border border-dashed border-blue-500/50 rounded-full animate-spin-slow"></div>
                  <span className="text-blue-500 font-mono-cia text-[9px] uppercase tracking-[0.6em]">{t.tools.box_vacuum}</span>
               </div>
             )}
          </div>
        ) : (
          <div className="relative w-full h-full">
            {worries.map(w => (
              <div key={w.id} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center pointer-events-none select-none px-6">
                <div className="text-xl md:text-3xl font-black uppercase italic tracking-tighter animate-slow-pulverize break-words">
                   <span className="bg-gradient-to-b from-white via-slate-100 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                    {w.text}
                   </span>
                </div>
              </div>
            ))}
            {dust.map(d => (
              <div 
                key={d.id} 
                className="absolute rounded-full animate-dust-float" 
                style={{ 
                    left: `${d.x}%`, 
                    top: `${d.y}%`, 
                    width: `${d.size}px`, 
                    height: `${d.size}px`, 
                    backgroundColor: d.color, 
                    opacity: d.opacity,
                    '--vx': `${d.vx}vw`, 
                    '--vy': `${d.vy}vh`,
                    '--delay': `${d.delay}s`
                  } as React.CSSProperties} 
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slow-pulverize {
          0% { opacity: 0; transform: scale(0.9); filter: blur(10px); }
          10% { opacity: 1; transform: scale(1); filter: blur(0px); }
          40% { transform: scale(1.02) rotate(0.5deg); filter: brightness(1.2); }
          60% { filter: blur(2px) contrast(1.5); }
          100% { opacity: 0; transform: scale(1.5) translateY(-60px); filter: blur(30px) grayscale(1); }
        }
        @keyframes dust-float {
          0% { opacity: 0; transform: translate(0, 0) scale(1); }
          20% { opacity: 0.8; }
          100% { opacity: 0; transform: translate(var(--vx), var(--vy)) scale(0.2); }
        }
        .animate-slow-pulverize { animation: slow-pulverize 4s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
        .animate-dust-float { animation: dust-float 10s ease-out forwards; animation-delay: var(--delay); }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default EnergyConversionBox;
